import { useEffect, useRef, type RefObject } from 'react'

interface Point {
    x: number
    y: number
}

interface Rect {
    x: number
    y: number
    w: number
    h: number
}

interface UseFocusOnNodeOptions {
    containerRef: RefObject<HTMLDivElement | null>
    worldRectsById: Record<string, Rect>
    contentScale: number
    contentOffset: Point
    setContentScale: (v: number | ((p: number) => number)) => void
    setContentOffset: (v: Point | ((p: Point) => Point)) => void
    sessionId: string | null
}

const ANIMATION_DURATION_MS = 300

function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3)
}

export function useFocusOnNode({
    containerRef,
    worldRectsById,
    contentScale,
    contentOffset,
    setContentScale,
    setContentOffset,
    sessionId,
}: UseFocusOnNodeOptions): void {
    const worldRectsRef = useRef(worldRectsById)
    worldRectsRef.current = worldRectsById

    const scaleRef = useRef(contentScale)
    scaleRef.current = contentScale

    const offsetRef = useRef(contentOffset)
    offsetRef.current = contentOffset

    const animRafRef = useRef<number | undefined>(undefined)

    useEffect(() => {
        if (!sessionId) return

        const handler = (e: Event) => {
            const { nodeId, sessionId: eventSessionId } = (e as CustomEvent).detail
            if (eventSessionId !== sessionId) return

            const el = containerRef.current
            if (!el) return

            // Cancel any in-flight animation
            if (animRafRef.current !== undefined) {
                cancelAnimationFrame(animRafRef.current)
                animRafRef.current = undefined
            }

            let tries = 0
            let waitRaf: number | undefined

            const tryFocus = () => {
                const rect = worldRectsRef.current[nodeId]
                const container = el.getBoundingClientRect()

                if (rect && container.width > 0) {
                    const fitScaleW = container.width / (rect.w * 1.6)
                    const fitScaleH = container.height / (rect.h * 1.6)
                    const targetScale = Math.max(
                        0.3,
                        Math.min(2.0, Math.min(fitScaleW, fitScaleH))
                    )

                    const cx = rect.x + rect.w / 2
                    const cy = rect.y + rect.h / 2
                    const targetX = Math.round(container.width / 2 - targetScale * cx)
                    const targetY = Math.round(container.height / 2 - targetScale * cy)

                    // Capture starting values for animation
                    const startScale = scaleRef.current
                    const startX = offsetRef.current.x
                    const startY = offsetRef.current.y
                    const startTime = performance.now()

                    const animate = (now: number) => {
                        const elapsed = now - startTime
                        const t = Math.min(1, elapsed / ANIMATION_DURATION_MS)
                        const ease = easeOutCubic(t)

                        const curScale = startScale + (targetScale - startScale) * ease
                        const curX = startX + (targetX - startX) * ease
                        const curY = startY + (targetY - startY) * ease

                        setContentScale(curScale)
                        setContentOffset({ x: Math.round(curX), y: Math.round(curY) })

                        if (t < 1) {
                            animRafRef.current = requestAnimationFrame(animate)
                        } else {
                            animRafRef.current = undefined
                        }
                    }

                    animRafRef.current = requestAnimationFrame(animate)
                    return
                }

                if (tries++ < 12) {
                    waitRaf = requestAnimationFrame(tryFocus)
                }
            }

            tryFocus()

            return () => {
                if (waitRaf !== undefined) cancelAnimationFrame(waitRaf)
            }
        }

        window.addEventListener('canvas:focus-node', handler)
        return () => {
            window.removeEventListener('canvas:focus-node', handler)
            if (animRafRef.current !== undefined) {
                cancelAnimationFrame(animRafRef.current)
            }
        }
    }, [sessionId, containerRef, setContentScale, setContentOffset])
}
